import React, { useEffect, useRef, useState } from "react";
import { C } from "../constants";

/**
 * Show / hide / rename table columns. Data stays in the plan; only the view changes.
 */
export default function TableColumnsMenu({ columns = [], isOn, toggle, header, rename, reset }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const hiddenCount = columns.filter((c) => !isOn(c.id)).length;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Customise visible columns"
        style={{
          padding: "7px 12px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          border: `1px solid ${open || hiddenCount ? C.primary : C.borderStrong}`,
          background: open ? C.primary : "#fff",
          color: open ? "#fff" : C.text,
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          boxShadow: open ? "none" : "0 1px 2px rgba(15,23,42,0.04)",
        }}
      >
        <i className="ti ti-adjustments" style={{ fontSize: 15 }} />
        Customise
        {hiddenCount ? (
          <span style={{
            fontSize: 10,
            fontWeight: 800,
            background: open ? "rgba(255,255,255,0.2)" : C.primarySoft,
            color: open ? "#fff" : C.primaryDark,
            borderRadius: 999,
            padding: "1px 7px",
          }}>
            {hiddenCount}
          </span>
        ) : null}
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 8px)",
            zIndex: 50,
            width: 340,
            maxHeight: 420,
            overflow: "hidden",
            background: "#fff",
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{
            padding: "12px 14px 10px",
            borderBottom: `1px solid ${C.border}`,
            background: "#F8FAFC",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 800, color: C.text }}>
              <i className="ti ti-columns" style={{ fontSize: 16, color: C.primary }} />
              Table columns
            </div>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4, lineHeight: 1.45 }}>
              Hide a column or edit its heading. The plan data is not deleted.
            </div>
          </div>
          <div style={{ padding: 10, overflowY: "auto", display: "grid", gap: 6 }}>
            {columns.map((col) => {
              const on = isOn(col.id);
              const icon = col.icon || "ti-layout-navbar";
              return (
                <div
                  key={col.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "36px 1fr auto",
                    gap: 8,
                    alignItems: "center",
                    padding: "8px 8px",
                    borderRadius: 10,
                    border: `1px solid ${on ? C.border : C.border}`,
                    background: on ? "#fff" : "#F8FAFC",
                    opacity: on ? 1 : 0.78,
                  }}
                >
                  <span style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: C.primarySoft, color: C.primary,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <i className={`ti ${icon}`} style={{ fontSize: 15 }} />
                  </span>
                  <input
                    type="text"
                    value={header(col.id, col.label)}
                    disabled={!on}
                    onChange={(e) => rename(col.id, e.target.value)}
                    aria-label={`Rename ${col.label}`}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      padding: "7px 8px",
                      fontSize: 12.5,
                      fontWeight: 650,
                      border: `1px solid ${C.border}`,
                      borderRadius: 7,
                      color: C.text,
                      background: on ? "#fff" : "#F3F4F6",
                    }}
                  />
                  <button
                    type="button"
                    disabled={!!col.required}
                    title={col.required ? "This column stays visible" : on ? "Hide column" : "Show column"}
                    onClick={() => toggle(col.id, columns)}
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      border: `1px solid ${on ? C.border : C.primary}55`,
                      background: on ? "#fff" : C.primarySoft,
                      color: col.required ? C.faint : C.primary,
                      cursor: col.required ? "not-allowed" : "pointer",
                    }}
                  >
                    <i className={`ti ${on ? "ti-eye" : "ti-eye-off"}`} style={{ fontSize: 16 }} />
                  </button>
                </div>
              );
            })}
          </div>
          <div style={{
            padding: "8px 12px 12px",
            borderTop: `1px solid ${C.border}`,
            display: "flex",
            justifyContent: "flex-end",
          }}>
            <button
              type="button"
              onClick={reset}
              style={{
                border: "none",
                background: "transparent",
                color: C.primary,
                fontSize: 12,
                fontWeight: 750,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <i className="ti ti-refresh" style={{ fontSize: 14 }} />
              Restore defaults
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
